import {
  getFocusStyle,
  getTheme,
  IIconProps,
  IStackProps,
  ITheme,
  mergeStyleSets,
  // Separator,
  Stack,
} from "@fluentui/react";
import { log } from "missionlog";
import { FunctionComponent, useEffect, useState } from "react";
import React = require("react");
import { useMutation, useQuery } from "react-query";
import { limaLogTag } from "../../../../../limaCommon/limaLog";
import limaTasksStore from "../../../../../store/limaTasksStore";
import { Task, Task2Task } from "../../../../../types/Task";
import { Task_withActaXcopy } from "../../../../../types/Task_withActaXcopy";
import { User2Task_withActaXcopy } from "../../../../../types/User2Task_withActaXcopy";
import limaStore from "../../../../../store/limaStore";
import { ItemToItem } from "../../../../../types/ItemToItem";
import { LIMA_ITEM_TYPE } from "../../../../../types/BASE_Enums";
import limaLoadeStore from "../../../../../store/limaLoaderStore";
import { httpGetAuth, httpPostAuth, httpPutAuth, HttpResponse } from "../../../../../api/httpAPI";
import { urlGetTaskLinkedToTaskList, urlPostDeleteTaskLinkedToTask } from "../../../../../api/apilinks";
import TaskListItem from "./TaskListItem";
import { LimaHideableSection } from "../../../../../limaComponents/LimaHideableSection";
import { LimaLoadingComponnet } from "../../../../../limaComponents/LimaLoadingComponnet";
import { LimaNotice } from "../../../../../limaComponents/LimaNotice";
import { useTranslation } from "react-i18next";

interface Task2TaskComponentProps {
  taskId: string;
  showComponent?: boolean;
  updateTaskLinkedCount: (number) => void;
}

const columnProps: Partial<IStackProps> = {
  tokens: { childrenGap: 0 },
  styles: { root: { width: "100%" } },
};

const theme: ITheme = getTheme();
const { palette, semanticColors, fonts } = theme;
const RemoveLinkIcon: IIconProps = { iconName: "RemoveLink" };
const AddLinkIcon: IIconProps = { iconName: "AddLink" };

const classNames = mergeStyleSets({
  // wrapper: {
  //   background: "lightgrey",
  // },
  itemCell: [
    getFocusStyle(theme, { inset: -1 }),
    {
      minHeight: 36,
      paddingBottom: 10,
      boxSizing: "border-box",
      borderBottom: `1px solid ${semanticColors.bodyDivider}`,
      display: "flex",
      selectors: {
        "&:hover": { background: palette.neutralLight },
      },
    },
  ],
  listWrapper: {
    width: "100%",
    maxHeight: "30vh",
    overflow: "auto",
  },
  itemImage: {
    flexShrink: 0,
  },
  itemContent: {
    marginLeft: 10,
    overflow: "hidden",
    flexGrow: 1,
  },
  itemName: [
    fonts.small,
    {
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      // textDecoration: "line-through",
      fontWeight: "bold",
    },
  ],
  itemIndex: {
    fontSize: fonts.small.fontSize,
    color: palette.neutralTertiary,
    marginBottom: 10,
  },
  chevron: {
    alignSelf: "center",
    marginLeft: 10,
    color: palette.neutralTertiary,
    fontSize: fonts.large.fontSize,
    flexShrink: 0,
    cursor: "pointer",
  },
});

const Task2TaskComponent: FunctionComponent<Task2TaskComponentProps> = ({
  taskId,
  showComponent,
  updateTaskLinkedCount,
}: Task2TaskComponentProps) => {
  const { mutateAsync: mutateAddRemoveLinking } = useAddTask2TaskQuery();
  const [linkedTasksInner, setLinkedTasksInner] = useState<Task[] | null>(null);
  const { isLoading, isFetching, isError, refetch } = useQuery(
    ["getTaskLinked", taskId],
    () => getTaskLinked(limaStore.userId, taskId),
    {
      enabled: false,
      onSuccess: (data: Task[]) => {
        log.info(limaLogTag.UserAddEdit, "useQuery/onSuccess data,type ", data);
        updateTaskLinkedCount(data?.length);
        setLinkedTasksInner(data);
      },
    }
  );
  const { t } = useTranslation(["tptasks"]);

  const onAddHandler = (taskIdOnAddHndlr: string, taskTo: Task) => {
    log.info(limaLogTag.Task2Task, `call onAddHandler ${taskIdOnAddHndlr} -> ${taskTo._key}`);
    void mutateAddRemoveLinking({
      taskId: taskIdOnAddHndlr,
      toTaskId: taskTo._key,
      userId: limaStore.userId,
      createOrDelete: true,
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    }).then((_data: Task2Task) => {
      setLinkedTasksInner([...linkedTasksInner, taskTo]);
    });
  };

  const onRemoveHandler = (taskIdOnRemoveHndlr: string, taskToId: string) => {
    log.info(limaLogTag.Task2Task, `call onRemoveHandler ${taskIdOnRemoveHndlr} -> ${taskToId}`);
    void mutateAddRemoveLinking({
      taskId: taskIdOnRemoveHndlr,
      toTaskId: taskToId,
      userId: limaStore.userId,
      createOrDelete: false,
    }).then((data: Task2Task) => {
      setLinkedTasksInner(linkedTasksInner.filter((item: Task) => item._key !== data.taskto._key));
    });
  };

  const onRenderCellTaskToAdd = (taskIdOnRenderTaskAdd: string, item: Task_withActaXcopy): JSX.Element => {
    return (
      <TaskListItem
        onAction={() => onAddHandler(taskIdOnRenderTaskAdd, item)}
        actionIcon={AddLinkIcon}
        onClickHandler={(itemOnClickTaskItem) => {
          log.info(limaLogTag.Task2Task, `onRenderCellTaskToAdd clicked `, itemOnClickTaskItem);
        }}
        item={item}
        actionEnabled={true}
      />
    );
  };

  const onRenderCellTaskToRemove = (taskIdOnRender: string, item: Task): JSX.Element => {
    return (
      <TaskListItem
        onAction={() => onRemoveHandler(taskIdOnRender, item._key)}
        actionIcon={RemoveLinkIcon}
        onClickHandler={(itemOnClick) => {
          log.info(limaLogTag.Task2Task, `onRenderCellTaskToRemove clicked `, itemOnClick);
        }}
        item={item}
        actionEnabled={true}
      />
    );
  };

  //-------------------------
  // Reactions
  //-------------------------
  useEffect(() => {
    void refetch();
  }, [taskId]);

  useEffect(() => {
    if (updateTaskLinkedCount !== undefined && linkedTasksInner !== null)
      updateTaskLinkedCount(linkedTasksInner.length);
  }, [linkedTasksInner]);

  return (
    <>
      {showComponent ? (
        <Stack horizontalAlign="start" {...columnProps} className={classNames.listWrapper}>
          <Stack.Item className={classNames.listWrapper}>
            {isLoading || isFetching ? (
              LimaLoadingComponnet(3)
            ) : isError ? (
              <LimaNotice>ERROR LOADING LINKED TASKS</LimaNotice>
            ) : (
              linkedTasksInner && linkedTasksInner.map((item: Task) => onRenderCellTaskToRemove(taskId, item))
            )}
          </Stack.Item>
          <LimaHideableSection name={t("tptasks:taskedit.taskitem2.linking.showhidelinkedLbl")}>
            <Stack.Item className={classNames.listWrapper}>
              {limaTasksStore.tasksList?.map((item: User2Task_withActaXcopy) => {
                if (taskId === item.task_withActaXcopy._key) {
                  log.info(limaLogTag.Task2Task, `onRenderCellTaskToAdd remove self `);
                  return null;
                } else if (
                  linkedTasksInner &&
                  linkedTasksInner.filter(
                    (innerTaskLinkedItem: Task) => innerTaskLinkedItem._key === item.task_withActaXcopy._key
                  ).length > 0
                ) {
                  log.info(limaLogTag.Task2Task, `onRenderCellTaskToAdd remove already linked `);
                  return null;
                } else {
                  log.info(limaLogTag.Task2Task, `onRenderCellTaskToAdd should render non linked and not self `);
                  return onRenderCellTaskToAdd(taskId, item.task_withActaXcopy);
                }
              })}
            </Stack.Item>
          </LimaHideableSection>
        </Stack>
      ) : (
        <></>
      )}
    </>
  );
};

export default Task2TaskComponent;

//----------------------
//   REACT QUERY
//----------------------

type Task2taskUser = {
  taskId: string;
  toTaskId: string;
  userId: string;
  createOrDelete: boolean;
};

const useAddTask2TaskQuery = () => {
  return useMutation(createOrDeleteTask2Task, {
    onMutate: ({ taskId, toTaskId, userId, createOrDelete }: Task2taskUser) => {
      // A mutation is about to happen!
      log.info(limaLogTag.Task2Task, "call mutate useAskToPathUserQuery ", taskId, toTaskId, userId, createOrDelete);
      return { id: 1 };
    },
    onSuccess: (data: Task2Task) => {
      log.info(limaLogTag.Task2Task, "call mutate useAskToPathUserQuery ", data);
    },
  });
};

//------ QUERY ---------------

const createOrDeleteTask2Task = async ({
  taskId,
  toTaskId,
  userId,
  createOrDelete,
}: Task2taskUser): Promise<Task2Task> => {
  log.info(limaLogTag.Task2Task, "fetching createTask2Task", taskId, toTaskId, userId);

  if (
    taskId === undefined ||
    taskId === null ||
    taskId === "" ||
    toTaskId === undefined ||
    toTaskId === null ||
    toTaskId === "" ||
    userId === undefined ||
    userId === null ||
    userId === ""
  ) {
    log.warn(limaLogTag.Task2Task, "no taskId or toTaskId or userId", taskId, toTaskId, userId);
    return null;
  }

  const item2Item: ItemToItem = {
    itemtype_from: LIMA_ITEM_TYPE.TASK,
    itemid_from: taskId,
    itemtype_to: LIMA_ITEM_TYPE.TASK,
    itemid_to: toTaskId,
    userid: userId,
  };
  const loaderID = limaLoadeStore.add(`ask to create task to task linking`);

  let response: HttpResponse<Task2Task>;
  try {
    if (createOrDelete === true) {
      response = await httpPostAuth<Task2Task>(urlPostDeleteTaskLinkedToTask(taskId), item2Item, loaderID.ac);
    } else {
      response = await httpPutAuth<Task2Task>(urlPostDeleteTaskLinkedToTask(taskId), item2Item, loaderID.ac);
    }
    log.info(limaLogTag.UserLinkingPage, "fetching data createTask2Task response", response);
    if (!response.ok) {
      throw response;
    }
    if (response.parsedBody !== undefined) {
      return response.parsedBody;
    }
    throw new Error("no data");
  } catch (error) {
    throw new Error(error);
  } finally {
    limaLoadeStore.remove(loaderID.k);
  }
};

const getTaskLinked = async (userId: string, taskId: string): Promise<Task[]> => {
  log.info(limaLogTag.TaskItem, "fetching data getTaskLinked", userId, taskId);
  if (userId == undefined || taskId == null) {
    log.warn(limaLogTag.TaskItem, "getTaskLinked: no task id or userid");
    return new Promise((reject) => {
      reject(null);
    });
  }
  const loaderID = limaLoadeStore.add("Getting getTaskLinked from DB");
  try {
    const response = await httpGetAuth<Task2Task[]>(urlGetTaskLinkedToTaskList(taskId), loaderID.ac);

    log.info(limaLogTag.TaskItem, "fetching data getTaskLinked response ...");
    if (!response.ok) throw response;
    if (response.parsedBody !== undefined) {
      return response.parsedBody.map((item: Task2Task): Task => item.taskto);
    }

    throw new Error("no data");
  } catch (error) {
    throw new Error(error);
  } finally {
    limaLoadeStore.remove(loaderID.k);
  }
};
